import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, IndianRupee, ShieldCheck, UserCheck, Sparkles, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { inr, fdate } from "../lib/format";
import { PageHeader, FormDialog, ConfirmDialog, DataTable, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";

export default function Commission() {
  const { user, isAdmin, isDirector, isStaff, isAgent } = useAuth();
  const [activeTab, setActiveTab] = useState(
    isStaff ? "staff_comm" : "allotment"
  ); // allotment | rules | ledger | staff_comm
  const [rules, setRules] = useState([]);
  const [sales, setSales] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ open: false, initial: null });
  const [del, setDel] = useState(null);
  const [pay, setPay] = useState(null);
  const [allotModal, setAllotModal] = useState({ open: false, sale: null, amount: "", notes: "" });

  const load = useCallback(async () => {
    try {
      const [rRes, sRes, pRes] = await Promise.all([
        api.get("/commission-rules"),
        api.get("/sales"),
        api.get("/projects"),
      ]);
      setRules(rRes.data || []);
      setSales((sRes.data || []).filter((s) => s.status !== "cancelled"));
      setProjects(pRes.data || []);
    } catch (err) {
      toast.error(errMsg(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fields = [
    {
      name: "project_id",
      label: "Project Name",
      type: "select",
      required: true,
      options: projects.map((p) => ({ value: p.id, label: p.name })),
      placeholder: "Select Project...",
    },
    {
      name: "applies_to",
      label: "Applies To",
      type: "text",
      required: true,
      placeholder: "e.g. General Manager, Deputy Director, Associate, All",
      hint: "Type designation, role, or staff category manually",
    },
    {
      name: "commission_type",
      label: "Commission Type",
      type: "text",
      required: true,
      placeholder: "e.g. 2%, ₹10,000, 50,000",
      hint: "Type commission rate or amount manually",
    },
    {
      name: "plot_size",
      label: "Plot Size",
      type: "text",
      placeholder: "e.g. 200 sq.yds or All",
    },
    {
      name: "plot_number",
      label: "Plot Number",
      type: "text",
      placeholder: "e.g. 101, GV-120 or All",
    },
    {
      name: "name",
      label: "Rule Name (Optional)",
      type: "text",
      placeholder: "e.g. Special Offer (or leave blank to auto-name)",
    },
  ];

  const submit = async (v) => {
    const proj = projects.find((p) => p.id === v.project_id);
    const projName = proj ? proj.name : "";
    const commType = (v.commission_type || "").trim();
    let numVal = 0;
    let ruleType = "fixed";
    if (commType) {
      const match = commType.replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
      if (match) {
        numVal = parseFloat(match[1]) || 0;
      }
      if (commType.includes("%")) {
        ruleType = "percentage";
      }
    }

    const payload = {
      name: v.name?.trim() || `${projName || "Project"} - ${v.applies_to || "General"}`,
      project_id: v.project_id || "",
      project_name: projName,
      applies_to: v.applies_to || "All Designations",
      level: (v.applies_to || "").toLowerCase().includes("leader") ? "team_leader" : "agent",
      commission_type: commType,
      plot_size: v.plot_size || "",
      plot_number: v.plot_number || "",
      rule_type: ruleType,
      value: numVal,
      basis: "sale_value",
      active: form.initial ? form.initial.active : true,
    };
    if (form.initial?.id) {
      await api.put(`/commission-rules/${form.initial.id}`, payload);
      toast.success("Commission rule updated");
    } else {
      await api.post("/commission-rules", payload);
      toast.success("Commission rule created");
    }
    load();
  };

  const paySubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/commission/pay", { sale_id: pay.sale.id, who: pay.who, amount: parseFloat(pay.amount) || 0 });
      toast.success("Commission marked as paid");
      setPay(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const handleAllotSubmit = async (e) => {
    e.preventDefault();
    if (!allotModal.sale) return;
    try {
      const commStr = (allotModal.amount || "").toString().trim();
      let amt = 0;
      if (commStr.includes("%")) {
        const pct = parseFloat(commStr.replace("%", "")) || 0;
        amt = (allotModal.sale.sale_amount * pct) / 100;
      } else {
        amt = parseFloat(commStr.replace(/[^\d.]/g, "")) || 0;
      }

      await api.post("/commission/allot-staff", {
        sale_id: allotModal.sale.id,
        staff_commission: amt,
        notes: allotModal.notes || "",
      });

      toast.success(`Commission of ${inr(amt)} successfully allotted to ${allotModal.sale.staff_name || "Staff"}!`);
      setAllotModal({ open: false, sale: null, amount: "", notes: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  // Calculations for Staff View
  const staffSales = sales.filter((s) => s.staff_code === user.agent_code || (isStaff && !s.staff_code));
  const staffTotalAllotted = staffSales.reduce((sum, s) => sum + (s.staff_commission || 0), 0);
  const staffTotalPaid = staffSales.reduce((sum, s) => sum + (s.staff_commission_paid || 0), 0);
  const staffTotalPending = Math.max(0, staffTotalAllotted - staffTotalPaid);

  // Calculations for Director / Admin Allotment View
  const totalAllottedByDir = sales.reduce((sum, s) => sum + (s.staff_commission || 0), 0);
  const pendingAllotmentCount = sales.filter((s) => !s.staff_commission || s.staff_commission_status === "pending").length;

  return (
    <div data-testid="commission-page" className="space-y-6">
      <PageHeader
        title={
          isStaff
            ? "My Staff Commissions"
            : isDirector
            ? "Commission Allotment Hub"
            : "Commission Hub"
        }
        sub={
          isStaff
            ? `Commissions allotted to you by Director on closed project sales (${user.name} · ${user.designation || "Staff"})`
            : isDirector
            ? `Assign and manage commission on project sales to your staff members (${user.name} · Director)`
            : "Manage Director commission allotments, commission rules, and payment ledgers"
        }
        testid="commission-header"
      >
        {isAdmin && activeTab === "rules" && (
          <Button
            className="bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm"
            onClick={() => setForm({ open: true, initial: null })}
            data-testid="add-rule-button"
          >
            <Plus className="mr-1 h-4 w-4" /> Add Rule
          </Button>
        )}
      </PageHeader>

      {/* Tabs navigation for Admin & Director */}
      {(isAdmin || isDirector) && (
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          <button
            onClick={() => setActiveTab("allotment")}
            className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${
              activeTab === "allotment"
                ? "bg-slate-900 text-white shadow-sm"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            🎯 Staff Commission Allotment ({sales.length} Sales)
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => setActiveTab("rules")}
                className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${
                  activeTab === "rules"
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                📋 Project Commission Rules ({rules.length})
              </button>
              <button
                onClick={() => setActiveTab("ledger")}
                className={`rounded-lg px-4 py-2 text-xs font-semibold transition-all ${
                  activeTab === "ledger"
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                📊 General Ledger & Payouts
              </button>
            </>
          )}
        </div>
      )}

      {/* --- STAFF VIEW: MY COMMISSIONS --- */}
      {isStaff && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Total Allotted Commission
              </span>
              <p className="mt-2 font-num text-2xl font-bold text-emerald-700">{inr(staffTotalAllotted)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Allotted by Director on team sales</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Paid Out
              </span>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{inr(staffTotalPaid)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Disbursed to date</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Pending Payout
              </span>
              <p className="mt-2 font-num text-2xl font-bold text-amber-700">{inr(staffTotalPending)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Awaiting payout disbursement</p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="font-display text-base font-semibold text-slate-900 mb-3">
              Sales Credited to You & Recruited Agents
            </h3>
            <DataTable
              testid="staff-comm-table"
              columns={[
                { key: "date", label: "Date", render: (r) => fdate(r.date) },
                {
                  key: "project",
                  label: "Project & Plot",
                  render: (r) => (
                    <div>
                      <p className="font-semibold text-slate-900">{r.project_name}</p>
                      <p className="text-xs text-slate-500 font-num">Plot {r.plot_number}</p>
                    </div>
                  ),
                },
                {
                  key: "selling_agent",
                  label: "Selling Agent",
                  render: (r) => (
                    <div>
                      <span className="text-xs font-medium text-slate-800">{r.agent_name || r.agent_code}</span>
                      <p className="text-[10px] text-slate-400">Agent ID: {r.agent_code}</p>
                    </div>
                  ),
                },
                { key: "customer_name", label: "Customer" },
                { key: "sale_amount", label: "Sale Value", align: "right", render: (r) => inr(r.sale_amount) },
                {
                  key: "staff_commission",
                  label: "Allotted Commission",
                  align: "right",
                  render: (r) => (
                    <span className="font-num text-sm font-bold text-emerald-700">
                      {inr(r.staff_commission || 0)}
                    </span>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (r) =>
                    r.staff_commission_status === "allotted" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> Allotted
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 border border-amber-200">
                        <Clock className="h-3 w-3" /> Pending Allotment
                      </span>
                    ),
                },
                {
                  key: "notes",
                  label: "Director's Note",
                  render: (r) => (
                    <span className="text-xs text-slate-600 italic">
                      {r.staff_commission_notes || "-"}
                    </span>
                  ),
                },
              ]}
              rows={staffSales}
            />
          </div>
        </div>
      )}

      {/* --- DIRECTOR & ADMIN VIEW: STAFF COMMISSION ALLOTMENT --- */}
      {(isAdmin || isDirector) && activeTab === "allotment" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Total Hierarchy Sales
              </span>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">
                {inr(sales.reduce((sum, s) => sum + (s.sale_amount || 0), 0))}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">{sales.length} Closed Deals</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Total Staff Commission Allotted
              </span>
              <p className="mt-2 font-num text-2xl font-bold text-emerald-700">{inr(totalAllottedByDir)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Assigned by Director to Staff</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Pending Allotment
              </span>
              <p className="mt-2 font-num text-2xl font-bold text-amber-700">{pendingAllotmentCount}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Sales needing commission assignment</p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display text-base font-semibold text-slate-900">
                  Project Sales & Staff Commission Allotment
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Director assigns commission for each project sale to respective staff members.
                </p>
              </div>
            </div>

            <DataTable
              testid="director-allotment-table"
              columns={[
                { key: "date", label: "Date", render: (r) => fdate(r.date) },
                {
                  key: "project",
                  label: "Project & Plot",
                  render: (r) => (
                    <div>
                      <p className="font-semibold text-slate-900">{r.project_name}</p>
                      <p className="text-xs text-slate-500 font-num">Plot {r.plot_number}</p>
                    </div>
                  ),
                },
                {
                  key: "selling_agent",
                  label: "Selling Agent",
                  render: (r) => (
                    <div>
                      <span className="text-xs font-medium text-slate-800">{r.agent_name || r.agent_code}</span>
                      <p className="text-[10px] text-slate-400">{r.agent_code}</p>
                    </div>
                  ),
                },
                {
                  key: "staff_in_charge",
                  label: "Responsible Staff",
                  render: (r) => (
                    <div>
                      <span className="text-xs font-semibold text-purple-700">
                        {r.staff_name || r.staff_code || "Unassigned"}
                      </span>
                      {r.staff_code && <p className="text-[10px] text-slate-400">ID: {r.staff_code}</p>}
                    </div>
                  ),
                },
                { key: "sale_amount", label: "Sale Value", align: "right", render: (r) => inr(r.sale_amount) },
                {
                  key: "staff_commission",
                  label: "Allotted Staff Comm.",
                  align: "right",
                  render: (r) => (
                    <div>
                      <span className="font-num text-sm font-bold text-emerald-700">
                        {inr(r.staff_commission || 0)}
                      </span>
                    </div>
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  render: (r) =>
                    r.staff_commission_status === "allotted" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="h-3 w-3" /> Allotted
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 border border-amber-200">
                        <Clock className="h-3 w-3" /> Pending
                      </span>
                    ),
                },
                {
                  key: "notes",
                  label: "Director's Note",
                  render: (r) => (
                    <span className="text-xs text-slate-500 italic max-w-[150px] truncate block">
                      {r.staff_commission_notes || "-"}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  label: "Action",
                  render: (r) => (
                    <Button
                      size="sm"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs shadow-sm h-7"
                      onClick={() =>
                        setAllotModal({
                          open: true,
                          sale: r,
                          amount: r.staff_commission || "",
                          notes: r.staff_commission_notes || "",
                        })
                      }
                    >
                      <Sparkles className="mr-1 h-3 w-3" />
                      {r.staff_commission_status === "allotted" ? "Edit Allotment" : "Allot Comm."}
                    </Button>
                  ),
                },
              ]}
              rows={sales}
            />
          </div>
        </div>
      )}

      {/* --- ADMIN ONLY: COMMISSION RULES VIEW --- */}
      {isAdmin && activeTab === "rules" && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {rules.map((r) => (
              <div
                key={r.id}
                className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                data-testid={`rule-card-${r.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-display font-semibold text-slate-900">{r.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {r.project_name || "All Projects"}
                      {r.applies_to ? ` · ${r.applies_to}` : r.level ? ` · ${r.level.replace("_", " ")}` : ""}
                      {r.plot_size ? ` · ${r.plot_size}` : ""}
                      {r.plot_number ? ` · Plot ${r.plot_number}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Edit rule"
                      data-testid={`edit-rule-${r.id}`}
                      onClick={() => setForm({ open: true, initial: r })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Delete rule"
                      className="text-red-600"
                      data-testid={`delete-rule-${r.id}`}
                      onClick={() => setDel(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="mt-3 font-num text-2xl font-bold text-emerald-700">
                  {r.commission_type || (r.rule_type === "percentage" ? `${r.value}%` : inr(r.value))}
                </p>
                <button
                  className={`mt-2 text-xs font-semibold ${r.active ? "text-emerald-700" : "text-slate-400"}`}
                  data-testid={`toggle-rule-${r.id}`}
                  onClick={async () => {
                    await api.put(`/commission-rules/${r.id}`, { ...r, active: !r.active });
                    load();
                  }}
                >
                  {r.active ? "● Active" : "○ Inactive"}
                </button>
              </div>
            ))}
            {rules.length === 0 && <p className="py-8 text-sm text-slate-400">No commission rules yet</p>}
          </div>
        </div>
      )}

      {/* --- ADMIN ONLY: GENERAL COMMISSION LEDGER --- */}
      {isAdmin && activeTab === "ledger" && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <h2 className="font-display text-lg font-medium text-slate-800">Master Commission Ledger</h2>
          <DataTable
            testid="commission-table"
            columns={[
              { key: "date", label: "Date", render: (r) => fdate(r.date) },
              { key: "project_name", label: "Project / Plot", render: (r) => `${r.project_name} · ${r.plot_number}` },
              { key: "agent_name", label: "Selling Agent" },
              { key: "staff_name", label: "Staff In-Charge", render: (r) => r.staff_name || r.staff_code || "-" },
              { key: "sale_amount", label: "Sale Amount", align: "right", render: (r) => inr(r.sale_amount) },
              { key: "agent_commission", label: "Agent Comm.", align: "right", render: (r) => inr(r.agent_commission) },
              {
                key: "staff_commission",
                label: "Staff Comm.",
                align: "right",
                render: (r) => inr(r.staff_commission || 0),
              },
              {
                key: "actions",
                label: "Payout Actions",
                render: (r) => (
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`pay-agent-${r.id}`}
                      disabled={r.agent_commission - r.agent_commission_paid <= 0}
                      className="text-xs text-emerald-700 hover:bg-emerald-50 border-emerald-300"
                      onClick={() =>
                        setPay({ sale: r, who: "agent", amount: r.agent_commission - r.agent_commission_paid })
                      }
                    >
                      Pay Agent
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`pay-team-${r.id}`}
                      disabled={(r.staff_commission || 0) - (r.staff_commission_paid || 0) <= 0}
                      className="text-xs text-purple-700 hover:bg-purple-50 border-purple-300"
                      onClick={() =>
                        setPay({
                          sale: r,
                          who: "staff",
                          amount: (r.staff_commission || 0) - (r.staff_commission_paid || 0),
                        })
                      }
                    >
                      Pay Staff
                    </Button>
                  </div>
                ),
              },
            ]}
            rows={sales}
          />
        </div>
      )}

      {/* --- ALLOT COMMISSION MODAL (DIRECTOR / ADMIN) --- */}
      <Dialog open={allotModal.open} onOpenChange={(o) => setAllotModal({ ...allotModal, open: o })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Director Commission Allotment</DialogTitle>
          </DialogHeader>
          {allotModal.sale && (
            <form onSubmit={handleAllotSubmit} className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Project / Plot:</span>
                  <span className="font-semibold text-slate-800">
                    {allotModal.sale.project_name} · Plot {allotModal.sale.plot_number}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Customer:</span>
                  <span className="font-medium text-slate-800">{allotModal.sale.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Selling Agent:</span>
                  <span className="font-medium text-slate-800">
                    {allotModal.sale.agent_name || allotModal.sale.agent_code}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Responsible Staff:</span>
                  <span className="font-semibold text-purple-700">
                    {allotModal.sale.staff_name || allotModal.sale.staff_code || "Staff Member"}
                  </span>
                </div>
                <div className="flex justify-between pt-1 border-t border-slate-200">
                  <span className="text-slate-600 font-medium">Sale Value:</span>
                  <span className="font-bold text-slate-900 font-num">{inr(allotModal.sale.sale_amount)}</span>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Commission Allotment (₹ or %) *
                </Label>
                <Input
                  className="mt-1.5"
                  placeholder="e.g. 50000 or 1.5% or ₹35,000"
                  value={allotModal.amount}
                  onChange={(e) => setAllotModal({ ...allotModal, amount: e.target.value })}
                  required
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Director's wish: Enter exact amount in ₹ or percentage of sale value.
                </p>
              </div>

              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Director's Message / Notes (Optional)
                </Label>
                <Textarea
                  className="mt-1.5 text-xs"
                  placeholder="e.g. Excellent teamwork on closing this premium villa plot deal"
                  value={allotModal.notes}
                  onChange={(e) => setAllotModal({ ...allotModal, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAllotModal({ open: false, sale: null, amount: "", notes: "" })}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm">
                  <Sparkles className="mr-1.5 h-4 w-4" /> Save Allotment
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* --- PAY COMMISSION MODAL --- */}
      <Dialog open={!!pay} onOpenChange={() => setPay(null)}>
        <DialogContent className="max-w-sm" data-testid="pay-commission-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Pay {pay?.who === "agent" ? "Agent" : "Staff"} Commission
            </DialogTitle>
          </DialogHeader>
          {pay && (
            <form onSubmit={paySubmit} className="space-y-4">
              <p className="text-sm text-slate-500">
                {pay.sale.project_name} · Plot {pay.sale.plot_number} ·{" "}
                {pay.who === "agent" ? pay.sale.agent_name : pay.sale.staff_name}
              </p>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Amount (₹)</Label>
                <Input
                  className="mt-1.5"
                  type="number"
                  step="any"
                  value={pay.amount}
                  data-testid="pay-commission-amount"
                  onChange={(e) => setPay({ ...pay, amount: e.target.value })}
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPay(null)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="pay-commission-submit">
                  <IndianRupee className="mr-1 h-4 w-4" /> Mark Paid
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* --- CREATE / EDIT COMMISSION RULE (ADMIN) --- */}
      <FormDialog
        open={form.open}
        onOpenChange={(o) => setForm({ open: o, initial: null })}
        title={form.initial ? "Edit Commission Rule" : "Add Commission Rule"}
        fields={fields}
        initial={
          form.initial
            ? {
                ...form.initial,
                commission_type:
                  form.initial.commission_type ||
                  (form.initial.rule_type === "percentage" ? `${form.initial.value}%` : `${form.initial.value}`),
                applies_to:
                  form.initial.applies_to ||
                  (form.initial.level === "team_leader" ? "Team Leader" : "All Designations"),
                plot_size: form.initial.plot_size || "",
                plot_number: form.initial.plot_number || "",
              }
            : null
        }
        onSubmit={submit}
        testid="rule-form"
      />

      <ConfirmDialog
        open={!!del}
        onOpenChange={() => setDel(null)}
        title="Delete Rule"
        message={`Delete commission rule "${del?.name}"?`}
        onConfirm={async () => {
          await api.delete(`/commission-rules/${del.id}`);
          toast.success("Rule deleted");
          load();
        }}
        testid="rule-delete-confirm"
      />
    </div>
  );
}
